import { useState } from "react";
import { format } from "date-fns";
 import { ArrowUpDown, Pencil, Trash2, Users } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { UserSortField, SortDirection, UserTagInfo } from "@/hooks/useAdminUsers";
import { getTagColorClass } from "@/hooks/useUserTags";
 import {
   Tooltip,
   TooltipContent,
   TooltipProvider,
   TooltipTrigger,
 } from "@/components/ui/tooltip";

interface UserMembership {
  credits_balance: number;
  is_unlimited: boolean;
  unlimited_expires_at: string | null;
  unlimited_starts_at: string | null;
  start_date: string | null;
  next_renewal_date: string | null;
}

 interface PartnerCodeInfo {
   code: string;
   partnerName: string;
   creditsGranted: number;
   redeemedAt: string;
 }

interface UserRole {
  role: string;
}

interface User {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  bio: string | null;
  member_type: string | null;
  referral_code: string | null;
  referred_by: string | null;
  created_at: string | null;
  joined_at: string | null;
  last_active_at: string | null;
  last_purchase_at: string | null;
  preferences: unknown;
  user_memberships: UserMembership | UserMembership[] | null;
  user_roles: UserRole[] | null;
  tags?: UserTagInfo[];
  eventCount?: number;
   partnerCode?: PartnerCodeInfo | null;
   referrerName?: string | null;
   referralCount?: number;
}

interface UsersTableProps {
  users: User[];
  isLoading: boolean;
  isEmpty: boolean;
  sortField: UserSortField;
  sortDirection: SortDirection;
  handleSort: (field: UserSortField) => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  selectedUserIds: Set<string>;
  onToggleUserSelection: (userId: string) => void;
  onTogglePageSelection: () => void;
  isPageSelected: boolean;
   onFilterByReferrer?: (referrerId: string, referrerName: string) => void;
}

const getMembership = (user: User): UserMembership | undefined => {
  if (!user.user_memberships) return undefined;
  if (Array.isArray(user.user_memberships)) return user.user_memberships[0];
  return user.user_memberships;
};

export const UsersTable = ({
  users,
  isLoading,
  isEmpty,
  sortField,
  sortDirection,
  handleSort,
  onEditUser,
  onDeleteUser,
  selectedUserIds,
  onToggleUserSelection,
  onTogglePageSelection,
  isPageSelected,
   onFilterByReferrer,
}: UsersTableProps) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const getInitials = (user: User) => {
    const first = user.first_name?.[0] || "";
    const last = user.last_name?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  const formatDate = (dateStr: string | null, includeTime = true) => {
    if (!dateStr) return "-";
    return format(new Date(dateStr), includeTime ? "M/d/yyyy, h:mm:ss a" : "M/d/yyyy");
  };

  const getUnlimitedStatus = (membership: UserMembership | undefined) => {
    if (!membership?.is_unlimited) return "-";
    const expiresAt = membership.unlimited_expires_at;
    if (!expiresAt) return "-";
    const isExpired = new Date(expiresAt) <= new Date();
    return (
      <span className={cn("font-medium", isExpired ? "text-muted-foreground" : "text-success")}>
        {isExpired ? "Expired" : formatDate(expiresAt)}
      </span>
    );
  };

  const isAdmin = (user: User) => {
    return user.user_roles?.some((r) => r.role === "admin");
  };

  const displayName = (user: User) => {
    const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    return name || "Unknown";
  };

  const SortableHeader = ({
    field,
    children,
    className,
  }: {
    field: UserSortField;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead
      className={cn("cursor-pointer hover:bg-muted/50 transition-colors", className)}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown
          className={cn(
            "h-3 w-3",
            sortField === field ? "text-secondary" : "text-muted-foreground"
          )}
        />
      </div>
    </TableHead>
  );

  return (
    <>
      {/* User Details Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-4 pb-3 border-b">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={selectedUser.avatar_url || undefined} />
                  <AvatarFallback>{getInitials(selectedUser)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{displayName(selectedUser)}</p>
                  <p className="text-muted-foreground text-xs">{selectedUser.email}</p>
                </div>
              </div>

              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">User ID</span>
                <span className="font-mono text-xs">{selectedUser.id}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Phone</span>
                <span>{selectedUser.phone || "-"}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Referral Code</span>
                <span className="font-mono">{selectedUser.referral_code || "-"}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Referred By</span>
                 <span>{selectedUser.referrerName || "-"}</span>
              </div>
               <div className="flex justify-between py-2 border-b">
                 <span className="text-muted-foreground">Partner Code</span>
                 <span className="font-mono">{selectedUser.partnerCode?.code || "-"}</span>
               </div>
               {selectedUser.partnerCode && (
                 <div className="flex justify-between py-2 border-b">
                   <span className="text-muted-foreground">Partner</span>
                   <span>{selectedUser.partnerCode.partnerName}</span>
                 </div>
               )}
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Joined</span>
                <span>{formatDate(selectedUser.created_at)}</span>
              </div>
              <div className="flex justify-between py-2 border-b">
                <span className="text-muted-foreground">Member Type</span>
                <span className="capitalize">{selectedUser.member_type || "member"}</span>
              </div>

              {selectedUser.bio && (
                <div className="py-2 border-b">
                  <p className="text-muted-foreground mb-1">Bio</p>
                  <p>{selectedUser.bio}</p>
                </div>
              )}

              {getMembership(selectedUser) && (
                <div className="pt-2">
                  <p className="font-medium mb-2">Membership</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Credits</span>
                      <span className="font-medium">{getMembership(selectedUser)!.credits_balance}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Unlimited</span>
                      <span>
                        {getMembership(selectedUser)!.is_unlimited
                          ? `Until ${formatDate(getMembership(selectedUser)!.unlimited_expires_at)}`
                          : "No"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Start</span>
                      <span>{formatDate(getMembership(selectedUser)!.start_date)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Renewal</span>
                      <span>{formatDate(getMembership(selectedUser)!.next_renewal_date)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Table>
        <TableHeader>
          <TableRow className="bg-muted">
            <TableHead className="w-10">
              <Checkbox
                checked={isPageSelected}
                onCheckedChange={onTogglePageSelection}
                aria-label="Select all on page"
              />
            </TableHead>
            <SortableHeader field="name">User</SortableHeader>
            <SortableHeader field="email">Email</SortableHeader>
            <TableHead>Phone</TableHead>
            <SortableHeader field="created_at" className="text-right">Joined</SortableHeader>
            <SortableHeader field="last_active_at" className="text-right">Last Active</SortableHeader>
            <SortableHeader field="last_purchase_at" className="text-right">Last Purchase</SortableHeader>
            <TableHead>Tags</TableHead>
            <SortableHeader field="eventCount" className="text-right">Events</SortableHeader>
             <SortableHeader field="partnerCode">Partner Code</SortableHeader>
             <SortableHeader field="referredBy">Referred By</SortableHeader>
             <SortableHeader field="referralCount" className="text-right">Referrals</SortableHeader>
            <TableHead className="text-center">Type</TableHead>
            <SortableHeader field="credits_balance" className="text-right">Credits</SortableHeader>
            <SortableHeader field="unlimited_expires_at" className="text-right">Unlimited Until</SortableHeader>
            <TableHead className="text-center">Role</TableHead>
            <TableHead className="text-center">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => {
            const membership = getMembership(user);
            const isSelected = selectedUserIds.has(user.id);
            return (
              <TableRow key={user.id} className={cn("h-9", isSelected && "bg-primary/5")}>
                <TableCell className="py-1">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => onToggleUserSelection(user.id)}
                    aria-label={`Select ${displayName(user)}`}
                  />
                </TableCell>
                <TableCell className="py-1">
                  <button
                    onClick={() => setSelectedUser(user)}
                    className="flex items-center gap-2 hover:underline text-left"
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={user.avatar_url || undefined} />
                      <AvatarFallback className="text-xs">{getInitials(user)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium whitespace-nowrap">{displayName(user)}</span>
                  </button>
                </TableCell>
                <TableCell className="text-muted-foreground py-1 whitespace-nowrap">
                  {user.email || "-"}
                </TableCell>
                <TableCell className="text-muted-foreground py-1 whitespace-nowrap">
                  {user.phone || "-"}
                </TableCell>
                <TableCell className="text-right py-1 whitespace-nowrap">
                  {formatDate(user.created_at)}
                </TableCell>
                <TableCell className="text-right py-1 whitespace-nowrap text-muted-foreground">
                  {formatDate(user.last_active_at)}
                </TableCell>
                <TableCell className="text-right py-1 whitespace-nowrap text-muted-foreground">
                  {formatDate(user.last_purchase_at)}
                </TableCell>
                <TableCell className="py-1">
                  <div className="flex items-center gap-1 flex-nowrap">
                    {user.tags?.slice(0, 3).map((tag) => (
                      <Badge 
                        key={tag.id} 
                        variant="outline" 
                        className={cn("text-xs whitespace-nowrap", getTagColorClass(tag.color))}
                      >
                        {tag.name}
                      </Badge>
                    ))}
                    {(user.tags?.length || 0) > 3 && (
                      <Badge variant="outline" className="text-xs whitespace-nowrap">
                        +{(user.tags?.length || 0) - 3}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right py-1 font-medium whitespace-nowrap">
                  {user.eventCount || 0}
                </TableCell>
                 <TableCell className="py-1">
                   {user.partnerCode ? (
                     <TooltipProvider>
                       <Tooltip>
                         <TooltipTrigger asChild>
                           <Badge variant="outline" className="text-xs cursor-help bg-violet-500/10 text-violet-700 border-violet-500/30">
                             {user.partnerCode.code}
                           </Badge>
                         </TooltipTrigger>
                         <TooltipContent side="top" className="max-w-xs">
                           <div className="space-y-1 text-xs">
                             <p><span className="text-muted-foreground">Partner:</span> {user.partnerCode.partnerName}</p>
                             <p><span className="text-muted-foreground">Credits:</span> {user.partnerCode.creditsGranted}</p>
                             <p><span className="text-muted-foreground">Redeemed:</span> {formatDate(user.partnerCode.redeemedAt, false)}</p>
                           </div>
                         </TooltipContent>
                       </Tooltip>
                     </TooltipProvider>
                   ) : (
                     <span className="text-muted-foreground">-</span>
                   )}
                 </TableCell>
                 <TableCell className="py-1">
                   {user.referrerName ? (
                     <button
                       className="text-left hover:underline text-primary whitespace-nowrap text-sm"
                       onClick={() => onFilterByReferrer?.(user.referred_by!, user.referrerName!)}
                     >
                       {user.referrerName}
                     </button>
                   ) : (
                     <span className="text-muted-foreground">-</span>
                   )}
                 </TableCell>
                 <TableCell className="text-right py-1">
                   {user.referralCount && user.referralCount > 0 ? (
                     <Badge 
                       variant="outline" 
                       className="text-xs cursor-pointer bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
                       onClick={() => onFilterByReferrer?.(user.id, `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Unknown")}
                     >
                       <Users className="h-3 w-3 mr-1" />
                       {user.referralCount}
                     </Badge>
                   ) : (
                     <span className="text-muted-foreground">-</span>
                   )}
                 </TableCell>
                <TableCell className="text-center py-1">
                  <Badge variant="outline" className="text-xs capitalize">
                    {user.member_type || "member"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right py-1 font-medium whitespace-nowrap">
                  {membership?.credits_balance ?? 0}
                </TableCell>
                <TableCell className="text-right py-1 whitespace-nowrap">
                  {getUnlimitedStatus(membership)}
                </TableCell>
                <TableCell className="text-center py-1">
                  {isAdmin(user) && (
                    <Badge variant="destructive" className="text-xs">Admin</Badge>
                  )}
                </TableCell>
                <TableCell className="text-center py-1">
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditUser(user);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteUser(user);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}

          {/* Loading state */}
          {isLoading && (
            <TableRow>
               <TableCell colSpan={17} className="text-center text-muted-foreground">
                Loading users...
              </TableCell>
            </TableRow>
          )}

          {/* Empty state */}
          {isEmpty && (
            <TableRow>
               <TableCell colSpan={17} className="text-center text-muted-foreground">
                No users found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};
